import React, { useEffect, useState } from 'react'
import classnames from 'classnames'
import toastr from 'toastr'
import Popup from '@SiteComponents/Popup'
import Loader from '@SiteComponents/Loader'
import Button from '@SiteComponents/Button'
import ExternalTokenForm from '@SiteComponents/ExternalTokenForm'
import MultiselectionDropdown from '@SiteComponents/MultiselectionDropdown'

import {
  IExternalToken,
} from '@SiteModules/ExternalToken/types'

import {
  IProjectSharingResponseBody,
  IProjectSharingUser,
} from '@SiteModules/Project/types'

import {
  fetchProjectSharing,
  updateBulkProjectSharing,
  updateProjectSharing,
} from '@SiteModules/Project/api'

import './ProjectSharingPopup.scss'
import 'toastr/build/toastr.css'

interface ProjectSharingPopupProps {
  readonly projectIds: string[]
  readonly subtitleId?: number
  readonly includePublicLink?: boolean
  readonly externalTokens?: IExternalToken[]
  readonly loadingExternalToken?: boolean
  closePopup?(): void
  copyPublicLink?(externalTokenUrl: string): void
  deletePublicLink?(externalTokenId: string): void
  setValidityOfExternalToken?(validityInDays: number): void
}

type TSharingMode = 'private' | 'workspace'

const ProjectSharingPopup: React.FC<ProjectSharingPopupProps> = props => {
  const {
    projectIds,
    includePublicLink,
    subtitleId,
    externalTokens,
    loadingExternalToken,
  } = props

  const [sharingData, setSharingData] = useState<IProjectSharingResponseBody | null>(null)
  const [sharingMode, setSharingMode] = useState<TSharingMode>('private')
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [loadingError, setLoadingError] = useState(false)

  const isBulk = projectIds.length > 1
  const shouldShowPublicLink = Boolean(includePublicLink && subtitleId && externalTokens && props.copyPublicLink && props.deletePublicLink && props.setValidityOfExternalToken)
  const uniqueUserIds = (userIds: number[]) => Array.from(new Set(userIds))

  useEffect(() => {
    loadProjectSharing()
  }, [projectIds.join(',')])

  const loadProjectSharing = async () => {
    if (projectIds.length === 0) { return }

    setLoading(true)
    setLoadingError(false)

    try {
      const response = await fetchProjectSharing({ projectId: projectIds[0] })
      const data = response.data as IProjectSharingResponseBody

      setSharingData(data)
      setSharingMode(data.sharingMode)
      setSelectedUserIds(uniqueUserIds(data.ownerUserId ? [data.ownerUserId, ...data.projectUserIds] : data.projectUserIds))
    } catch (e) {
      setLoadingError(true)
      toastr.error('', 'Project access could not be loaded')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectPrivate = () => {
    setSharingMode('private')
  }

  const handleSelectWorkspace = () => {
    setSharingMode('workspace')
  }

  const handleSelectMembers = (nextSelectedMemberIndices: number[]) => {
    const ownerUserIds = ownerUserId ? [ownerUserId] : []
    const nextSelectedMemberUserIds = nextSelectedMemberIndices.reduce<number[]>((userIds, selectedMemberIndex) => {
      const user = selectableWorkspaceUsers[selectedMemberIndex]

      return user ? [...userIds, user.id] : userIds
    }, [])

    setSelectedUserIds([...ownerUserIds, ...nextSelectedMemberUserIds])
    setSharingMode('private')
  }

  const handleRemoveUser = (userId: number) => {
    if (userId === ownerUserId) { return }

    setSelectedUserIds(selectedUserIds.filter(selectedId => selectedId !== userId))
  }

  const handleRemoveUserClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    const userId = parseInt(e.currentTarget.dataset.userId || '', 10)

    if (!userId) { return }

    handleRemoveUser(userId)
  }

  const handleSave = async () => {
    setSaving(true)

    try {
      if (isBulk) {
        await updateBulkProjectSharing({
          projectIds,
          sharingMode,
          userIds: selectedUserIds,
        })
      } else {
        const response = await updateProjectSharing({
          projectId: projectIds[0],
          sharingMode,
          userIds: selectedUserIds,
        })
        setSharingData(response.data as IProjectSharingResponseBody)
      }

      toastr.success('', isBulk ? 'Projects shared' : 'Project sharing updated')
      props.closePopup?.()
    } catch (e) {
      toastr.error('', 'Project sharing could not be updated')
    } finally {
      setSaving(false)
    }
  }

  const workspaceMembers = sharingData?.workspaceUsers || []
  const ownerUserId = sharingData?.ownerUserId
  const sharingUsers = [...(sharingData?.users || []), ...workspaceMembers]
  const displayedUserIds = uniqueUserIds(ownerUserId ? [ownerUserId, ...selectedUserIds] : selectedUserIds)
  const selectedUsers = displayedUserIds.reduce<IProjectSharingUser[]>((users, userId) => {
    const user = sharingUsers.find(sharingUser => sharingUser.id === userId)

    return user && !users.some(selectedUser => selectedUser.id === user.id) ? [...users, user] : users
  }, [])
  const selectableWorkspaceUsers = workspaceMembers.filter(user => user.id !== ownerUserId)
  const selectedMemberIndices = selectedUserIds.reduce<number[]>((indices, userId) => {
    const index = selectableWorkspaceUsers.findIndex(user => user.id === userId)

    return index >= 0 ? [...indices, index] : indices
  }, [])
  const selectableUserNames = selectableWorkspaceUsers.map(user => user.name || user.email)
  const selectableUserEmails = selectableWorkspaceUsers.map(user => user.name ? user.email : '')

  const content = (
    <div
      className="ProjectSharingPopup"
      id="project-sharing-popup"
      data-testid="project-sharing-popup"
    >
      <div className="ProjectSharingPopup__header">
        <div>
          <div className="ProjectSharingPopup__eyebrow">
            {isBulk ? `${projectIds.length} projects selected` : 'Project sharing'}
          </div>
          <div className="ProjectSharingPopup__title">Share project</div>
        </div>
        <div className="ProjectSharingPopup__badge">Project-wide</div>
      </div>

      {loading ? (
        <div className="ProjectSharingPopup__loader">
          <Loader color="dark" />
        </div>
      ) : loadingError || !sharingData ? (
        <div className="ProjectSharingPopup__empty-members">
          You do not have permission to manage sharing for this project.
        </div>
      ) : (
        <React.Fragment>
          <div className="ProjectSharingPopup__body">
            <section className="ProjectSharingPopup__section">
              <div className="ProjectSharingPopup__section-title">Project access</div>
              <div className="ProjectSharingPopup__access-options">
                <button
                  type="button"
                  className={classnames('ProjectSharingPopup__access-option', { 'ProjectSharingPopup__access-option-active': sharingMode === 'private' })}
                  onClick={handleSelectPrivate}
                  data-testid="project-access-private-option"
                >
                  <span>Private</span>
                  <small>Only invited people and account admins can open the project.</small>
                </button>
                <button
                  type="button"
                  className={classnames('ProjectSharingPopup__access-option', { 'ProjectSharingPopup__access-option-active': sharingMode === 'workspace' })}
                  onClick={handleSelectWorkspace}
                  data-testid="project-access-workspace-option"
                >
                  <span>Workspace members</span>
                  <small>Every member of this workspace can open the full project.</small>
                </button>
              </div>
            </section>

            <section className="ProjectSharingPopup__section">
              <div className="ProjectSharingPopup__section-title">People with access</div>
              <div className="ProjectSharingPopup__member-picker">
                <div
                  className="ProjectSharingPopup__member-select-wrapper"
                  id="project-access-member-select-wrapper"
                  data-testid="project-access-member-select"
                >
                  <MultiselectionDropdown
                    availableOptions={selectableUserNames}
                    availableOptionDescriptions={selectableUserEmails}
                    selectedOptionIndices={selectedMemberIndices}
                    entityName="member"
                    classNameForClickOutsideListener="ProjectSharingPopup__member-dropdown"
                    idNameOfParentContainer="project-access-member-select-wrapper"
                    setSelectedOptionIndices={handleSelectMembers}
                    disabled={selectableWorkspaceUsers.length === 0 || sharingMode === 'workspace'}
                    portalContainerId="body"
                    showAllCounter
                  />
                  <div className="ProjectSharingPopup__member-picker-hint">
                    {sharingMode === 'workspace'
                      ? 'Workspace-wide access is on. Switch to Private to invite specific people.'
                      : selectableWorkspaceUsers.length > 0
                        ? 'Checked members appear below. Save sharing applies the changes.'
                        : 'Invite users to the workspace before adding them here.'}
                  </div>
                </div>
              </div>
              <div className="ProjectSharingPopup__members">
                {selectedUsers.length === 0 ? (
                  <div className="ProjectSharingPopup__empty-members">
                    No individual members added yet.
                  </div>
                ) : (
                  selectedUsers.map(user => (
                    <div className="ProjectSharingPopup__member" key={user.id}>
                      <div className="ProjectSharingPopup__avatar">
                        {user.email.charAt(0).toUpperCase()}
                      </div>
                      <div className="ProjectSharingPopup__member-copy">
                        <div>{user.name || user.email}</div>
                        <span>{user.email}</span>
                      </div>
                      {user.id === ownerUserId ? (
                        <div
                          className="ProjectSharingPopup__member-role"
                          data-testid={`project-access-owner-member-${user.id}`}
                        >
                          Owner
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={handleRemoveUserClick}
                          data-user-id={user.id}
                          data-testid={`project-access-remove-member-${user.id}`}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </section>

            {shouldShowPublicLink && (
              <section className="ProjectSharingPopup__section ProjectSharingPopup__public-link">
                <ExternalTokenForm
                  loading={!!loadingExternalToken}
                  externalTokens={externalTokens as IExternalToken[]}
                  copyToClipboard={props.copyPublicLink as (externalTokenUrl: string) => void}
                  deleteExternalToken={props.deletePublicLink as (externalTokenId: string) => void}
                  setValidityOfExternalToken={props.setValidityOfExternalToken as (validityInDays: number) => void}
                  renderedFromDashboard={false}
                  title="Public link"
                  description="This link only opens the current language, version, or clip. It does not share the full project."
                  copyButtonTestId="public-link-copy"
                  deleteButtonTestId="public-link-delete"
                  expiryInputTestId="public-link-expiry-days-input"
                  addExpiryDateTestId="public-link-add-expiry-date"
                  removeExpiryDateTestId="public-link-remove-expiry-date"
                />
              </section>
            )}
          </div>

          <div className="ProjectSharingPopup__footer">
            <Button
              styleType="brand"
              size="large"
              caption={saving ? 'Saving...' : 'Save sharing'}
              onClick={handleSave}
              disabled={saving}
              data-testid="project-sharing-save"
            />
          </div>
        </React.Fragment>
      )}
    </div>
  )

  if (!props.closePopup) {
    return content
  }

  return (
    <Popup
      className="ProjectSharingPopup__popup"
      closePopup={props.closePopup}
      zIndex={200000}
    >
      {content}
    </Popup>
  )
}

export default ProjectSharingPopup
